import { useState } from 'react';
import { Alert, Button, List, Radio, Tag } from 'antd';
import { useTranslation } from 'react-i18next';
import type { CaptiveDevice } from '../api/captive';
import { deviceQualifier, formatTime } from './format';

/**
 * Registered devices / de-register choice (Amendment 02 Part A). Shown only when
 * the account's device limit (>1) is fully used. The subscriber picks one device
 * to de-register; confirming replaces it with the current device (the core does
 * the de-register + register atomically via `register(replace_id)`).
 */
export function DeviceChoiceScreen({
  limit,
  devices,
  submitting,
  error,
  onConfirm,
}: {
  limit: number;
  devices: CaptiveDevice[];
  submitting: boolean;
  error: string | null;
  onConfirm: (deviceId: number) => void;
}) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState<number | null>(null);

  return (
    <>
      <div className="portal-title">{t('choice.title')}</div>
      <p className="portal-subtitle">{t('choice.subtitle')}</p>
      {limit > 0 && (
        <p style={{ textAlign: 'center', marginTop: -10, marginBottom: 16 }}>
          <Tag color="warning">{t('choice.limit', { limit })}</Tag>
        </p>
      )}
      {error && (
        <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} role="alert" />
      )}
      <Radio.Group
        style={{ display: 'block', width: '100%' }}
        value={selected ?? undefined}
        onChange={(e) => setSelected(e.target.value as number)}
      >
        <List
          size="small"
          bordered
          dataSource={devices}
          rowKey={(d) => d.id}
          style={{ marginBottom: 16 }}
          renderItem={(d) => {
            const qualifier = deviceQualifier(d);
            const last = formatTime(d.last_online_at);
            const reg = formatTime(d.registered_at);
            return (
              <List.Item onClick={() => setSelected(d.id)} style={{ cursor: 'pointer' }}>
                <Radio value={d.id} style={{ marginRight: 8 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div className="mono">{d.mac}</div>
                  {qualifier && (
                    <div className="mono" style={{ opacity: 0.7, fontSize: 12 }}>
                      {qualifier}
                    </div>
                  )}
                  <div style={{ opacity: 0.6, fontSize: 12 }}>
                    {t(`device.origin.${d.origin}`, d.origin)}
                    {reg ? ` · ${t('choice.registered')}: ${reg}` : ''}
                    {` · ${t('choice.lastOnline')}: ${last ?? t('choice.never')}`}
                  </div>
                </div>
              </List.Item>
            );
          }}
        />
      </Radio.Group>
      <Button
        type="primary"
        danger
        block
        disabled={selected === null}
        loading={submitting}
        onClick={() => selected !== null && onConfirm(selected)}
      >
        {t('choice.confirm')}
      </Button>
    </>
  );
}
